import React, { useState } from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';

import type { CustomStep, PatientIntakeStep } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../hooks/index.js';
import { generateUploadPromises, UploadFileModal, UploadFilesFormData, useFileUploader } from '../../../../file-upload';
import { usePatientIntakeStatusContext } from '../../hooks';
import { ViewDocumentModal, NoteDisplay } from '../../components';
import type { BaseActionPromptProps } from './CustomSteps.types.ts';
import { CustomStepActionPrompt } from './CustomStepActionPrompt.tsx';

type UploadMode = {
  stepId: string;
  isOpen: boolean;
  patientId: string;
  healthCategoryId: string;
  hint: {
    title: string;
    description: string;
    note: NonNullable<PatientIntakeStep['currentStatus']>['note'] | null;
  };
};

type UploadFileModalDescriptionProps = {
  uploadMode: UploadMode;
};

const UploadFileModalDescription: React.FC<UploadFileModalDescriptionProps> = ({ uploadMode }) => (
  <Alert severity="info" icon={false}>
    <AlertTitle>{uploadMode.hint?.title}</AlertTitle>
    <Typography variant="body2">{uploadMode.hint?.description}</Typography>

    {!!uploadMode.hint?.note && <NoteDisplay stepId={uploadMode.stepId} noteConfig={uploadMode.hint?.note} />}
  </Alert>
);

type UploadModalProps = {
  uploadMode: UploadMode | null;
  setUploadMode: React.Dispatch<React.SetStateAction<UploadMode | null>>;
};

const UploadModal: React.FC<UploadModalProps> = ({ uploadMode, setUploadMode }) => {
  const { user, userType } = useAuth();

  const { uploader } = useFileUploader();

  const handleFileUploadOnSubmit = async ({ files, description }: UploadFilesFormData) => {
    if (!uploadMode) {
      throw new Error('Upload mode not found');
    }

    if (!user || !userType || !user.email) {
      throw new Error('User not found');
    }

    const { healthCategoryId, stepId } = uploadMode;

    if (!healthCategoryId || !stepId) {
      throw new Error('Category ID or step ID not found');
    }

    const generateFileUploadData: Parameters<
      typeof generateUploadPromises<'PATIENT_DOCUMENT_UPLOAD'>
    >[0]['generateFileUploadData'] = ({ file }) => ({
      file,
      type: 'PATIENT_DOCUMENT_UPLOAD',
      ownerId: user.uid,
      uploaderId: user.uid,
      fileName: file.name,
      metadata: {
        patientId: user.uid,
        description: description.trim(),
        type: 'DISCHARGE_LETTER',
        data: {
          stepId,
          healthCategoryId,
        },
      },
    });

    return await generateUploadPromises({ files, uploader, generateFileUploadData });
  };

  const handleCloseOnClick = () => {
    setUploadMode(null);
  };

  if (!uploadMode) {
    return null;
  }

  return (
    <UploadFileModal
      isOpen
      noToast
      handleCloseOnClick={handleCloseOnClick}
      onSubmit={handleFileUploadOnSubmit}
      maxFiles={1}
      description={<UploadFileModalDescription uploadMode={uploadMode} />}
      defaultValues={{
        description: 'My discharge letter',
      }}
    />
  );
};

type UploadDocumentActionModalProps = {
  step: CustomStep['DISCHARGE_LETTER_UPLOAD'];
  uploadMode: UploadMode | null;
  setUploadMode: React.Dispatch<React.SetStateAction<UploadMode | null>>;
};

const UploadDocumentActionModal: React.FC<UploadDocumentActionModalProps> = ({ step, uploadMode, setUploadMode }) => {
  if (step.currentStatus?.status === 'COMPLETED') {
    return (
      <ViewDocumentModal
        step={step}
        type="PATIENT_DOCUMENT_UPLOAD"
        isOpen={!!uploadMode}
        onClose={() => setUploadMode(null)}
      />
    );
  }

  return <UploadModal uploadMode={uploadMode} setUploadMode={setUploadMode} />;
};

interface DischargeLetterActionProps extends BaseActionPromptProps {
  step: CustomStep['DISCHARGE_LETTER_UPLOAD'];
  activeHealthCategoryId: string;
}

export const DischargeLetterAction: React.FC<DischargeLetterActionProps> = ({
  step,
  activeHealthCategoryId,
  onEdit,
  onDelete,
  onToggleDisabled,
}) => {
  const { customSteps, patientId } = usePatientIntakeStatusContext();
  const [uploadMode, setUploadMode] = useState<UploadMode | null>(null);

  const handleOnClick = () => {
    setUploadMode({
      stepId: step.id,
      isOpen: true,
      patientId,
      healthCategoryId: activeHealthCategoryId,
      hint: {
        title: step.title,
        description: step.description,
        note: step.currentStatus?.note ?? null,
      },
    });
  };

  if (!customSteps) {
    return null;
  }

  return (
    <>
      <CustomStepActionPrompt
        step={step}
        description={step.description}
        onClick={handleOnClick}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleDisabled={onToggleDisabled}
      />

      <UploadDocumentActionModal step={step} uploadMode={uploadMode} setUploadMode={setUploadMode} />
    </>
  );
};
